import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlayIcon, SearchIcon, SendIcon } from 'lucide-react';
import { Slide } from '../Slide';
import { InView } from '../InView';
import { SlideHeadline } from '../SlideHeadline';
import { DeviceFrame } from '../../DeviceFrame';
import { heroOct, heroPromise, heroCredibility } from '../../../data/teaser';
import { featuredSnak, shelfSnaks, shelfThumb, stationFilters, stationPillars, stationScreenshot, stationSource } from '../../../data/deck';
import type { ShelfSnak, StationFilter } from '../../../data/deck';
export function StationSlide() {
  const [filter, setFilter] = useState<StationFilter>('All');
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return shelfSnaks.filter(snak => (filter === 'All' || snak.category === filter) && (!q || `${snak.title} ${snak.category}`.toLowerCase().includes(q)));
  }, [filter, query]);
  return <Slide id="station" data-id="element-256" data-mp="125l3zm6n8twp">
      <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16" data-id="element-257" data-mp="1237ctof341uf">
        <InView data-id="element-258" data-mp="17221v83nammz">
          <SlideHeadline lead="Every Snak on one shelf." follow="Every market picks what works." data-id="element-259" data-mp="s6vvul4juntn" />
        </InView>
        <InView delay={0.06} data-id="element-260" data-mp="x5fk4s6m8bp6">
          <p className="text-[14.5px] leading-[1.7] text-navy/65" data-id="element-261" data-mp="x4nnde1a87xb">
            Snakker Station stores every Snak in one place, so any country can see what is working elsewhere — then
            preview, reuse and localize it instead of starting over.
          </p>
        </InView>
      </div>

      <InView delay={0.12} y={26} className="mt-12" data-id="element-262" data-mp="9hflz76z5j0k">
        <div className="overflow-hidden rounded-[30px] bg-paper shadow-[0_50px_90px_-60px_rgba(11,42,74,0.6)] ring-1 ring-navy/[0.08]" data-id="element-263" data-mp="efh1ji56z07a">
          <div className="p-5 sm:p-7" data-id="element-264" data-mp="gr4g2idh0ix">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-6" data-id="element-265" data-mp="hsrl5cnxdh0">
              <div className="flex shrink-0 items-center gap-3" data-id="element-266" data-mp="4gi2mra8aixh">
                <span className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-navy text-mint" data-id="element-267" data-mp="2748clcme4t5m">
                  <PlayIcon className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-268" data-mp="22cgg6lgjzt7k" />
                </span>
                <span className="font-display text-[26px] font-semibold leading-none tracking-[-0.02em] text-navy" data-id="element-269" data-mp="1xex49s7bvsis">
                  Snakker Station
                </span>
              </div>
              <label className="flex flex-1 items-center gap-2.5 rounded-full bg-canvas px-4 py-2.5 focus-within:ring-2 focus-within:ring-blue" data-id="element-270" data-mp="2q1ri469n3tna">
                <SearchIcon className="h-4 w-4 text-navy/45" aria-hidden="true" data-id="element-271" data-mp="2l1wx2jcjpya5" />
                <span className="sr-only" data-id="element-272" data-mp="2g2trmxsnlq1n">Search Snaks</span>
                <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search — try “congress” or “follow-up”" className="w-full bg-transparent text-[13px] text-navy placeholder:text-navy/40 focus:outline-none" data-id="element-273" data-mp="2c200lvjwzv4j" />
              </label>
              <span className="shrink-0 text-[12.5px] font-semibold text-blue" data-id="element-274" data-mp="3gcju4589d4du">
                Discover. Preview. Reuse. Localize. Activate.
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2" data-id="element-275" data-mp="3q2mni6jakkcp">
              {stationFilters.map(item => <button key={item} type="button" onClick={() => setFilter(item)} aria-pressed={filter === item} className={['rounded-full px-3.5 py-1.5 text-[12px] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue', filter === item ? 'bg-navy text-paper' : 'bg-canvas text-navy hover:bg-canvas2'].join(' ')} data-id="element-276" data-mp="3v1zi5dgemvpu">
                  {item}
                </button>)}
              <span className="ml-auto hidden text-[11.5px] text-navy/45 md:inline" data-id="element-277" data-mp="1jdb1iu5pgjp3">
                {shelfSnaks.length + 1} Snaks · curated for global and local teams
              </span>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-[240px_minmax(0,0.8fr)_minmax(0,1.6fr)]" data-id="element-278" data-mp="1tb76t5byokfa">
              <div className="flex items-center justify-center rounded-[22px] bg-canvas2 px-4 py-5" data-id="element-279" data-mp="1ocnm1893teoi">
                <DeviceFrame label="Featured Snak preview" sizeClassName="h-[400px] w-[200px]" data-id="element-280" data-mp="10ma1s3wrgk4h">
                  <div className="flex h-full flex-col bg-paper" data-id="element-281" data-mp="vnyotkb65ti1">
                    <img src={heroOct.src} alt={heroOct.alt} className="block h-[150px] w-full object-cover" data-id="element-282" data-mp="vlm2kuydowz6" />
                    <div className="flex flex-1 flex-col px-4 pb-5 pt-4" data-id="element-283" data-mp="1aefp30c2iddc">
                      <p className="font-display text-[15px] font-semibold leading-tight text-navy" data-id="element-284" data-mp="15kld6j2pf2ot">{heroPromise}</p>
                      <p className="mt-2 text-[9.5px] leading-snug text-mut" data-id="element-285" data-mp="htz2ek1bf4b4">{heroCredibility}</p>
                      <span className="mt-auto rounded-full bg-navy py-2 text-center text-[10px] font-semibold text-paper" data-id="element-286" data-mp="cvedvhvfe92i">
                        Explore the evidence
                      </span>
                    </div>
                  </div>
                </DeviceFrame>
              </div>

              <div className="flex flex-col" data-id="element-287" data-mp="2ekbmforxgred">
                <span className="w-fit rounded-full bg-sky px-3 py-1 text-[11px] font-medium text-navy" data-id="element-288" data-mp="2jkpvna59m7dz">
                  {featuredSnak.category}
                </span>
                <h3 className="mt-3 font-display text-[26px] font-semibold leading-[1.05] tracking-[-0.02em] text-navy" data-id="element-289" data-mp="3ca52dnpwde43">
                  {featuredSnak.title}
                </h3>
                <p className="mt-1.5 text-[12.5px] text-navy/55" data-id="element-290" data-mp="2ycgmkuaosx5w">{featuredSnak.usedIn}</p>
                <span className="mt-6 text-[10px] uppercase tracking-[0.2em] text-navy/45" data-id="element-291" data-mp="32a5yw58c4dy3">Helps solve</span>
                <p className="mt-1.5 font-display text-[19px] font-medium leading-[1.25] text-navy" data-id="element-292" data-mp="1mxbch5887wc9">{featuredSnak.solves}</p>
                <div className="mt-4 flex flex-wrap gap-1.5" data-id="element-293" data-mp="3tj7oilaa7xyz">
                  {featuredSnak.channels.map(channel => <span key={channel} className="rounded-full border border-navy/20 px-3 py-1 text-[11.5px] text-navy" data-id="element-294" data-mp="3olfmuzju4xhl">
                      {channel}
                    </span>)}
                </div>
                <span className="mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-navy px-4 py-2.5 text-[12.5px] font-medium text-paper" data-id="element-295" data-mp="3eu1r92v08htq">
                  <SendIcon className="h-3.5 w-3.5 text-mint" aria-hidden="true" data-id="element-296" data-mp="35u6bekqi7ynd" />
                  Request adaptation
                </span>
              </div>

              <div data-id="element-297" data-mp="30v21frvr9wvz">
                <p className="font-display text-[17px] font-semibold text-navy" data-id="element-298" data-mp="2vvqkahrh8pdv">
                  More on the shelf <span className="font-normal text-navy/40" data-id="element-299" data-mp="z3gmdip1uxsf">· {visible.length}</span>
                </p>
                <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-300" data-mp="u4cc0oqe2u0i">
                  <AnimatePresence mode="popLayout" data-id="element-301" data-mp="u29cne3cjoj4">
                    {visible.map(snak => <motion.li key={snak.id} layout initial={{
                    opacity: 0,
                    scale: 0.96
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.96
                  }} transition={{
                    duration: 0.2,
                    ease: [0.23, 1, 0.32, 1]
                  }} data-id="element-302" data-mp="12j9z4flhkw4s">
                        <ShelfThumb snak={snak} data-id="element-303" data-mp="jobht85m4d2e" />
                        <p className="mt-2 font-display text-[13px] font-semibold leading-tight text-navy" data-id="element-304" data-mp="nnkf2mzv2t2p">{snak.title}</p>
                        <p className="mt-0.5 text-[11px] text-navy/50" data-id="element-305" data-mp="smf5ob42llxd">{snak.category}</p>
                      </motion.li>)}
                  </AnimatePresence>
                </ul>
                {visible.length === 0 && <p className="mt-6 text-[13px] text-navy/50" data-id="element-306" data-mp="244q257nrzaj1">No Snaks match that yet — try another filter.</p>}
              </div>
            </div>
          </div>

          <div className="grid gap-6 bg-navy px-5 py-7 sm:px-7 lg:grid-cols-[1fr_1fr_1fr_1.1fr] lg:items-center" data-id="element-307" data-mp="2u3ehdltpa2az">
            {stationPillars.map(pillar => <div key={pillar.label} data-id="element-308" data-mp="33wc300tzo7dt">
                <span className="text-[10px] uppercase tracking-[0.22em] text-mint" data-id="element-309" data-mp="33xdhkfa7rc0a">{pillar.label}</span>
                <p className="mt-1.5 text-[13px] leading-relaxed text-paper/80" data-id="element-310" data-mp="2z2r2o24xb1bg">{pillar.text}</p>
              </div>)}
            <p className="font-display text-[20px] font-semibold leading-[1.2] text-paper lg:text-right" data-id="element-311" data-mp="38uwsp6bm78o0">
              One shared platform. Central intelligence. Local empowerment.
            </p>
          </div>
        </div>
      </InView>
    </Slide>;
}
function ShelfThumb({
  snak
}: {
  snak: ShelfSnak;
}) {
  return <div className="relative overflow-hidden rounded-[12px] bg-canvas" style={{
    aspectRatio: `${shelfThumb.width} / ${shelfThumb.height}`
  }} data-id="element-312" data-mp="1sh6lvrt426ao">
      <img src={stationScreenshot} alt={`${snak.title} — ${snak.category} Snak thumbnail`} className="absolute block max-w-none" style={{
      width: `${stationSource.width / shelfThumb.width * 100}%`,
      left: `-${snak.x / shelfThumb.width * 100}%`,
      top: `-${snak.y / shelfThumb.height * 100}%`
    }} data-id="element-313" data-mp="2yzybyv0amsw" />
    </div>;
}